import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { ConfigStrip } from '@/components/runs/ConfigStrip';
import { FailureSection } from '@/components/runs/FailureSection';
import { InputSection } from '@/components/runs/InputSection';
import { JsonBlock } from '@/components/runs/JsonBlock';
import { Section } from '@/components/runs/Section';
import { cn } from '@/lib/cn';
import { formatDuration, formatRelative, shortId } from '@/lib/format';
import { stepTone } from '@/lib/runFormatters';
import type { ActivityStep } from '@/types/run';

export function Step({ step, index }: { step: ActivityStep; index: number }) {
  const [open, setOpen] = useState(false);
  const tone = stepTone(step.status);
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <li className="rounded-md border border-border bg-card/40">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-start gap-3 p-3 text-left transition-colors hover:bg-muted/30"
      >
        <Chevron className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <div
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[11px] font-medium',
            step.status === 'running'
              ? 'border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-300'
              : step.status === 'completed'
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                : step.status === 'failed' || step.status === 'timed_out'
                  ? 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300'
                  : 'border-border bg-muted text-muted-foreground',
          )}
        >
          {index}
        </div>
        <div className="flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{step.activity_type}</span>
            <Badge tone={tone}>{step.status}</Badge>
            {step.attempt > 1 && <Badge tone="warning">attempt {step.attempt}</Badge>}
            <span className="font-mono text-[11px] text-muted-foreground">
              {shortId(step.activity_id, 8, 4)}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span>scheduled {formatRelative(step.scheduled_at)}</span>
            {step.started_at && <span>started {formatRelative(step.started_at)}</span>}
            {step.completed_at && (
              <span>
                finished in{' '}
                {formatDuration(step.started_at ?? step.scheduled_at, step.completed_at)}
              </span>
            )}
            {!open && step.failure_message && (
              <span className="text-red-700 dark:text-red-300">— {step.failure_message.slice(0, 80)}</span>
            )}
          </div>
        </div>
      </button>

      {open && (
        <div className="space-y-3 border-t border-border/60 px-3 pb-3 pt-3">
          <ConfigStrip step={step} />
          {step.last_heartbeat && step.status === 'running' && (
            <Section label="Live heartbeat" tone="running">
              <JsonBlock value={step.last_heartbeat} />
            </Section>
          )}
          <InputSection input={step.input} />
          {step.status === 'completed' && (
            <Section label="Output" tone="success">
              {step.output === null || step.output === undefined ? (
                <p className="text-xs italic text-muted-foreground">
                  Activity returned no value (e.g., a void persist step).
                </p>
              ) : (
                <JsonBlock value={step.output} />
              )}
            </Section>
          )}
          {(step.status === 'failed' || step.status === 'timed_out') && step.failure && (
            <FailureSection failure={step.failure} />
          )}
        </div>
      )}
    </li>
  );
}
